import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import { useParams } from 'react-router-dom';
import DialogFrame from '../_parts/DialogFrame';
import {
    getAdvancedExportConfig,
    closeAdvancedExportDialog,
} from '../../../../redux/project/documents/sliceProjectAdvancedExportDialog';
import { DialogContent } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import Documents from '../_parts/Documents';
import { removeProjectDocumentsInfoItem } from '../../../../redux/project/documents/sliceProjectDocuments';
import AdvancedExportDialogActions from './_parts/AdvancedExportDialogActions';
import AdvancedExport, {
    IAdvancedExportErrorState,
    IAdvancedExportMainState,
    IExportAdditionalDocument,
} from '../_parts/AdvancedExport';

const initialMainState: IAdvancedExportMainState = {
    documentTypeName: '',
    includeInExport: [],
    additionalFiles: [],
};

const initialErrorState: IAdvancedExportErrorState = {
    documentTypeName: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'exportDocumentsDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const params = useParams();

    const { replicaId } = params;

    const { data: documents, loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );

    const { loading: loadingAdvancedExportConfig } = useSelector(
        (_store: IAppReduxStore) => _store.projectAdvancedExportDocuments,
    );

    const isDialogOpen = useSelector(
        (_store: IAppReduxStore) => _store.projectAdvancedExportDialog.isDialogOpen,
    );

    useEffect(() => {
        if (isDialogOpen) {
            dispatch(getAdvancedExportConfig({ replicaId }));
        }
    }, [isDialogOpen]);

    const [mainState, setMainState] = useState<IAdvancedExportMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<IAdvancedExportErrorState>(initialErrorState);

    const handleChange = ({
        field,
        value,
    }: {
        field: string;
        value: string | string[] | IExportAdditionalDocument | IExportAdditionalDocument[];
    }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const resetToInitialState = () => {
        setMainState(initialMainState);
        setShowErrorState(initialErrorState);
    };

    const onClose = () => {
        resetToInitialState();
        dispatch(closeAdvancedExportDialog());
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingInfoDocs || loadingAdvancedExportConfig}
                    documentSelection={
                        <Documents documents={documents} removeDocument={removeDocument} />
                    }
                    main={
                        <AdvancedExport
                            mainState={mainState}
                            handleChange={handleChange}
                            showErrorState={showErrorState}
                        />
                    }
                />
            </DialogContent>

            <AdvancedExportDialogActions
                setShowErrorState={setShowErrorState}
                mainState={mainState}
                onClose={onClose}
                isLoading={loadingAdvancedExportConfig}
                numberOfDocuments={documents.length}
            />
        </DialogFrame>
    );
};
